import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { agentsPath } from "@/utils/accountRoutes";
import {
  ReadinessUnavailableError,
  fetchReadiness,
  readinessQueryKey,
} from "../../agentSkills/readinessApi";
import { fetchPackages, packagesQueryKey } from "../../agentSkills/packagesApi";
import { spotlightTargetProps } from "@/components/featureSpotlight/spotlightTargetProps";

/**
 * Home entry to "Agentes do escritório": where the firm is and the one next
 * step (create the agents, connect the WhatsApp, finish what is missing).
 * It used to say "0 de 5 prontos", counting the law-firm workflows, which read
 * as "no agent created" right after creating five. Admins only (the API
 * answers 403 to everyone else), and silent when the check fails: the home is
 * not the place to report a readiness error.
 */

/** The card's sentence and action, from the readiness and whether agents exist. */
export function agentsCardState(readiness, created) {
  const { ready, total } = readiness.summary;
  const missingWhatsapp = (readiness.workflows || []).some((w) =>
    (w.checks || []).some((c) => c.key === "channel.whatsapp" && c.status !== "ok"),
  );
  if (!created) return { text: "Crie os agentes que atendem e fazem a triagem no WhatsApp.", action: "Criar agentes", etapa: null };
  if (missingWhatsapp) return { text: "Agentes criados. Conecte o WhatsApp para eles começarem a atender.", action: "Conectar WhatsApp", etapa: "sistemas" };
  if (ready < total) return { text: "Agentes criados. Algumas funções ainda precisam de configuração.", action: "Ver o que falta", etapa: "capacidades" };
  return { text: "Agentes criados e prontos para atender.", action: "Ver agentes", etapa: "capacidades" };
}
export default function AgentReadinessCard({ accountId, enabled = true }) {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: readinessQueryKey(accountId),
    queryFn: () => fetchReadiness(accountId),
    enabled: Boolean(accountId) && enabled,
    staleTime: 5 * 60_000,
    retry: (count, err) => !(err instanceof ReadinessUnavailableError) && count < 1,
  });

  const { data: listing, isLoading: loadingListing } = useQuery({
    queryKey: packagesQueryKey(accountId),
    queryFn: () => fetchPackages(accountId),
    enabled: Boolean(accountId) && enabled && Boolean(data?.summary?.total),
    staleTime: 5 * 60_000,
  });

  if (!data?.summary?.total || loadingListing) return null;

  const created = Boolean(listing?.packages?.some((p) => p.applied));
  const state = agentsCardState(data, created);
  const target = state.etapa ? `${agentsPath(accountId)}?etapa=${state.etapa}` : agentsPath(accountId);

  return (
    <button
      type="button"
      onClick={() => navigate(target)}
      {...spotlightTargetProps("dashboard.agent-readiness-card")}
      className="group mb-[18px] flex w-full items-center gap-4 rounded-[22px] border border-black/[.06] bg-white/60 px-8 py-5 text-left transition-colors hover:bg-white/80 dark:border-transparent dark:bg-white/[.03] dark:hover:bg-white/[.05]"
    >
      <div className="min-w-0 flex-1">
        <div className="text-[15px] font-semibold text-[#15161f] dark:text-white">Agentes do escritório</div>
        <div className="mt-0.5 text-[13px] text-[#5c5f70] dark:text-white/55">
          {state.text}
        </div>
      </div>
      <span className="flex items-center gap-1 text-[13px] font-medium text-[#12a06c] dark:text-[#7ee2a8]">
        {state.action}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
      </span>
    </button>
  );
}
